import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { onError } from '@apollo/client/link/error';
import { Monitor } from '@sortlist-frontend/mlm';
import { isBrowser, isEqual, merge } from '@sortlist-frontend/utils';
import { print } from 'graphql';
import { useMemo } from 'react';

import coreApiPossibleTypes from '../core-api/possibleTypes.json';
import federationApiPossibleTypes from '../federation/possibleTypes.json';
import publicApiPossibleTypes from '../public-api/possibleTypes.json';
import { customResolvers, customTypePolicies } from './customProperties';
import {
  coreApiUrl,
  federationApiUrl,
  isIgnoredGraphQLError,
  isUnauthorizedApolloError,
  makeAuthMiddleware,
  makeFragmentDeDupeLink,
  publicApiUrl,
} from './utils';

if (process.env.NODE_ENV !== 'production') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

type ApiSchema = 'core' | 'public' | 'federation';

const getDefaultApiUrl = (schema: ApiSchema) => {
  if (schema === 'core') {
    return coreApiUrl;
  }
  if (schema === 'federation') {
    return federationApiUrl;
  }
  return publicApiUrl;
};

const createHttpLink = (schema: ApiSchema, apiUrl?: string) =>
  new HttpLink({
    uri: (apiUrl ?? getDefaultApiUrl(schema)) + '/graphql',
  });

const browserApolloClients: Partial<Record<ApiSchema, ApolloClient<NormalizedCacheObject>>> = {};

const getPossibleTypes = (schema: ApiSchema) => {
  if (schema === 'core') {
    return coreApiPossibleTypes.possibleTypes;
  }
  if (schema === 'federation') {
    return federationApiPossibleTypes.possibleTypes;
  }
  return publicApiPossibleTypes.possibleTypes;
};

export const createApolloCache = (schema: ApiSchema) =>
  new InMemoryCache({
    possibleTypes: getPossibleTypes(schema),
    typePolicies: {
      // Add custom, client-facing only fields here if needed
      // e.g. enhancing an existing types
      ...customTypePolicies,
    },
  });

export const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors != null) {
    for (const error of graphQLErrors) {
      const { message, path } = error;
      if (message.includes('error_in_previous_query')) {
        return;
      }
      if (isIgnoredGraphQLError(operation.operationName, message)) {
        continue;
      }
      const exception = `[GraphQL error]: Message: ${message} for ${operation.operationName}`;

      const data = {
        fullQuery: print(operation.query),
        variables: operation.variables,
        originalError: error,
        operation,
        path: path?.join(' > '),
      };
      Monitor.captureException(exception, { extra: data, tags: { graphqlOperation: operation.operationName } });
    }
  }
  if (networkError != null && !isUnauthorizedApolloError({ networkError })) {
    const exception = `[Network error]: ${networkError} ${operation.operationName}`;

    const data = {
      fullQuery: print(operation.query),
      variables: operation.variables,
      originalError: networkError,
      operation,
    };
    Monitor.captureException(exception, { extra: data, tags: { graphqlOperation: operation.operationName } });
  }
});

// Lets an app inject extra links (e.g. an Accept-Language middleware sourced from
// its own schema's `me`). The factory receives the client's cache so a link can
// read app-specific state from it. Returned links are inserted just before the
// error link and HTTP terminator.
type ExtraLinksFactory = (cache: InMemoryCache) => ApolloLink[];

function createApolloClient(
  fetchAuthToken: () => Promise<string>,
  schema: ApiSchema,
  apiUrl?: string,
  extraLinks?: ExtraLinksFactory,
) {
  const cache = createApolloCache(schema);

  return new ApolloClient({
    connectToDevTools: true,
    ssrMode: !isBrowser(),
    link: from([
      makeFragmentDeDupeLink(),
      makeAuthMiddleware(fetchAuthToken),
      ...(extraLinks?.(cache) ?? []),
      errorLink,
      createHttpLink(schema, apiUrl),
    ]),
    cache,
    resolvers: {
      // Add custom resolvers if needed
      ...customResolvers,
    },
  });
}

type InitializeApolloBaseConfig = {
  apiUrl?: string;
  initialState?: NormalizedCacheObject | null;
  extraLinks?: ExtraLinksFactory;
};

type InitializeApolloCoreConfig<T> = T & {
  schema: 'core';
  fetchAuthToken: () => Promise<string>;
};

type InitializeApolloPublicConfig<T> = T & {
  schema: 'public';
};

type InitializeApolloFederationConfig<T> = T & {
  schema: 'federation';
  fetchAuthToken: () => Promise<string>;
};

type InitializeApolloConfig =
  | InitializeApolloCoreConfig<InitializeApolloBaseConfig>
  | InitializeApolloPublicConfig<InitializeApolloBaseConfig>
  | InitializeApolloFederationConfig<InitializeApolloBaseConfig>;

export function initializeApollo(config: InitializeApolloConfig) {
  const { schema, apiUrl, initialState, extraLinks } = config;

  const fetchAuthToken = isPublicSchemaConfig(config) ? async () => '' : config.fetchAuthToken;

  const _apolloClient = browserApolloClients[schema] ?? createApolloClient(fetchAuthToken, schema, apiUrl, extraLinks);

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState != null) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray: unknown[], sourceArray: unknown[]) => [
        ...sourceArray,
        ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
      ],
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') {
    return _apolloClient;
  }

  // Create the Apollo Client once in the client
  if (browserApolloClients[schema] == null) {
    browserApolloClients[schema] = _apolloClient;
  }

  return _apolloClient;
}

export function addApolloState(client?: ApolloClient<NormalizedCacheObject>, pageProps?: any) {
  if (client != null && pageProps?.props != null) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

type UseApolloBaseProps = {
  apiUrl?: string;
  pageProps: any;
  extraLinks?: ExtraLinksFactory;
};

type UseApolloProps =
  | InitializeApolloCoreConfig<UseApolloBaseProps>
  | InitializeApolloPublicConfig<UseApolloBaseProps>
  | InitializeApolloFederationConfig<UseApolloBaseProps>;

export function useApollo(props: UseApolloProps) {
  const { pageProps } = props;

  const state = pageProps?.[APOLLO_STATE_PROP_NAME];

  const fetchAuthToken = isPublicSchemaProps(props) ? async () => '' : props.fetchAuthToken;

  return useMemo(
    () =>
      initializeApollo({
        apiUrl: props.apiUrl,
        initialState: state,
        schema: props.schema,
        fetchAuthToken,
        extraLinks: props.extraLinks,
      }),
    [state],
  );
}

function isPublicSchemaConfig(
  config: InitializeApolloConfig,
): config is InitializeApolloPublicConfig<InitializeApolloBaseConfig> {
  return config.schema === 'public';
}

function isPublicSchemaProps(props: UseApolloProps): props is InitializeApolloPublicConfig<UseApolloBaseProps> {
  return props.schema === 'public';
}
